import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { useState, useRef, useCallback, useEffect } from 'react'
import SearchBar from '../../../baseComponents/SearchBar'
import Checkbox from '../../../forms/Checkbox'
import CustomSelect from '../../../forms/CustomSelect'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import useOnClickOutside from '../../../utils/hooks/useOnClickOut'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { OnChangeSearchBarAddOrderCustomer, OrderModelTypeExtended } from './types'
import { displayDateByString } from '../../../utils/globals/date'
import { TwinDictionary } from '../../../utils/globals/dictionary'

interface SelectorSearchBarOrderDeciderComponentProps { 
    translations: {
        title: string
        seeText: string,
        duplicateText: string
    }
    baseURL: string
    getDataURL: string
}

const SearchBarOrderCustomerDeciderComponent: React.FC<SelectorSearchBarOrderDeciderComponentProps> = ({ baseURL, getDataURL, translations }: SelectorSearchBarOrderDeciderComponentProps) => {
    const { data, setData } = useSelectorSearchBarOrderDeciderComponentLogic()
    const { t } = useTwinTranslation()
    const oldURL = baseURL + '/' + String(data?.id)
    if (data?.id) {
        const nameValue = data?.full_assigned_number + ' - ' + data?.Customer?.name
        return (
            <div className='flex flex-col'>
                <CustomSelect name='OrderId' value={data.id} icon={faTimes} readOnly={true} label={ translations.title } items={{ [data.id]: { name: nameValue } }} onClickIcon={() => setData(null)} className='mt-16' />
                <a href={oldURL} rel="noreferrer" target='_blank' className='cursor-pointer text-green-43 ml-auto mt-10'>{translations.seeText}</a>
                <Checkbox className='mt-20 block' name='updatePrice' label={t('updatePricesOfProducts', 'Actualizar precios de los productos')} defaultChecked={true}></Checkbox>
                <Checkbox className='mt-20 block' name='updateCopyProduct' label={t('updateNameAndDescriptions', 'Actualizar nombre y descripción de los productos')} defaultChecked={true}></Checkbox>
            </div>
        )
    }
    return (
        <SearchBarOrderCustomer className='mt-16' placeholder={translations.duplicateText} setData={setData} getDataURL={getDataURL} />
    )
}

const useSelectorSearchBarOrderDeciderComponentLogic = () => {
    const [data, setData] = useState<OrderModelTypeExtended | null>(null)
    return { setData, data }
}

interface SearchBarOrderCustomerProps extends SearchBarOrderCustomerLogicProps {
    title?: string
    className?: string
    placeholder?: string
}

const SearchBarOrderCustomer: React.FC<SearchBarOrderCustomerProps> = ({ title, className, placeholder, ...rest }) => {
    const { setSearchValue, items, searchValue, ref, myChangeSelectedItems } = useSearchBarOrderCustomerLogic({ ...rest })
    return (
        <div className={'relative ' + (className || '')} ref={ref}>
            {title ? <span className='block regular18 my-22'>
                {title}
            </span> : null}
            <SearchBar onChange={setSearchValue} {...{ searchValue, placeholder }} />
            {items && <SearchBarOrderCustomerResults orders={items} onSelectQuote={myChangeSelectedItems} className='w-full' />}
        </div>
    )
}

interface SearchBarOrderCustomerLogicProps {
    getDataURL: string
    setData: React.Dispatch<React.SetStateAction<OrderModelTypeExtended | null>>
}

const useSearchBarOrderCustomerLogic = ({ setData, getDataURL }: SearchBarOrderCustomerLogicProps) => {
    const ref = useRef<any>(null)
    const [searchValue, setSearchValue] = useState<string>('')
    const [items, setItems] = useState<OrderModelTypeExtended[] | null>(null)

    const resetSearchValue = useCallback(() => {
        setSearchValue('')
        setItems(null)
    }, [setSearchValue])

    const myChangeSelectedItems: OnChangeSearchBarAddOrderCustomer = useCallback((value) => {
        setData(value)
        resetSearchValue()
    }, [setData, resetSearchValue])

    const searchItems = useCallback(async (value: string) => {
        const searchedItems = await twinFetchPostJSON(getDataURL, {
            needPrices: true,
            where: {
                '$or': {
                    'full_assigned_number': {
                        '$iLike': `%${value}%`
                    },
                    'Customer.name': { '$iLike': `%${value}%` },
                }
            },
            limit: 15
        })
        setItems(searchedItems)
    }, [setItems, getDataURL])


    useOnClickOutside(ref, () => resetSearchValue())


    useEffect(() => {
        if (searchValue !== '') {
            searchItems(searchValue)
        } else {
            setItems(null)
        }
    }, [searchValue, searchItems])

    return { setSearchValue, items, searchValue, resetSearchValue, ref, myChangeSelectedItems }
}



interface SearchBarOrderCustomerResultsProps {
    orders: OrderModelTypeExtended[] | null
    onSelectQuote: OnChangeSearchBarAddOrderCustomer
    className?: string
}

const SearchBarOrderCustomerResults: React.FC<SearchBarOrderCustomerResultsProps> = ({ orders, onSelectQuote, className }) => {
    const renderThis: JSX.Element[] = []
    if (!orders) {
        return null
    }
    for (const key in orders) {
        const order = orders[key]
        renderThis.push(<SearchedQuoteCustomer key={key} order={order} onSelectQuote={onSelectQuote} />)
    }
    return (
        <div className={'searched_input_products ' + (className || '')}>
            <div className='results_searched_products'>
                {renderThis}
            </div>
        </div>
    )
}

interface SearchedQuoteCustomerProps {
    onSelectQuote: OnChangeSearchBarAddOrderCustomer
    order: OrderModelTypeExtended
}

const SearchedQuoteCustomer: React.FC<SearchedQuoteCustomerProps> = ({ order, onSelectQuote }) => {
    const customerName = order?.Customer?.name
    const reference = order.full_assigned_number
    //@ts-ignore
    const version = order?.version || ''
    const workingCenterName = order.WorkingCenter?.name
    const date = displayDateByString(order.processed_date || order.createdAt)
    let secondaryFied = reference
    if (version) {
        secondaryFied += '/' + version
    }
    let fields: TwinDictionary = {
        main: customerName,
        secondary: secondaryFied,
        date: date
    }
    return (
        <div className='searched_product' onClick={() => onSelectQuote(order)}>
            <span className='text-green-00 font-medium'>{fields.secondary}</span>
            <span className='mx-5'>-</span>
            <span className='twin_elipsis'>{fields.main}</span>
            <span className='mx-5'>-</span>
            <span>{fields.date}</span>
            <span className='mx-5'>-</span>
            <span className='twin_elipsis'>{workingCenterName}</span>
        </div>
    )
}


export default SearchBarOrderCustomerDeciderComponent